<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useNavigation } from '@/composables/useNavigation'
import { useDefaults } from 'vuetify'
import type { NavigationMenuItem } from './types'
import mdiChevronDown from '~icons/mdi/chevron-down'

const { t } = useI18n()
const { getMenuItemTo } = useNavigation()

const _props = defineProps<{
  items: NavigationMenuItem[]
  activeItem?: string
}>()
const props = useDefaults(_props)

defineEmits<{
  close: []
}>()

const isInitialized = ref(false)

onMounted(() => {
  requestAnimationFrame(() => {
    isInitialized.value = true
  })
})

const activeTabIndex = computed(() => {
  if (props.activeItem == null) return undefined
  const index = props.items.findIndex((item) => item.locale_name === props.activeItem)
  return index >= 0 ? index : undefined
})
</script>

<template>
  <div
    v-if="props.items.length"
    class="w-100 bg-grey-darkest"
  >
    <v-tabs
      :model-value="activeTabIndex"
      :slider-color="isInitialized ? 'brand-primary' : 'transparent'"
      height="48"
      class="w-100 px-5 max-width-header"
    >
      <template
        v-for="(sub, index) in props.items"
        :key="sub.locale_name"
      >
        <v-menu
          v-if="sub.list_menu?.length"
          open-on-click
          :open-delay="0"
          :close-delay="0"
          location="bottom"
          transition="slide-y-transition"
        >
          <template #activator="{ props: menuProps, isActive }">
            <v-tab
              v-bind="menuProps"
              :value="index"
            >
              {{ t(sub.locale_name) }}
              <v-icon
                :icon="mdiChevronDown"
                size="small"
                class="ml-1 chevron-rotate"
                :class="{ 'chevron-rotate--active': isActive }"
              />
            </v-tab>
          </template>

          <v-list
            nav
            density="compact"
            class="bg-grey-darkest pa-0"
          >
            <v-list-item
              v-for="item in sub.list_menu"
              :key="item.locale_name"
              :title="t(item.locale_name)"
              :to="getMenuItemTo(item)"
              class="text-body-medium"
            />
          </v-list>
        </v-menu>

        <v-menu
          v-else-if="sub.sub_menu?.length"
          open-on-click
          :open-delay="0"
          :close-delay="0"
          location="bottom"
          transition="slide-y-transition"
        >
          <template #activator="{ props: menuProps, isActive }">
            <v-tab
              v-bind="menuProps"
              :value="index"
            >
              {{ t(sub.locale_name) }}
              <v-icon
                :icon="mdiChevronDown"
                size="small"
                class="ml-1 chevron-rotate"
                :class="{ 'chevron-rotate--active': isActive }"
              />
            </v-tab>
          </template>

          <v-list
            nav
            density="compact"
            class="bg-grey-darkest pa-0"
          >
            <v-list-item
              v-for="child in sub.sub_menu"
              :key="child.locale_name"
              :title="t(child.locale_name)"
              :to="getMenuItemTo(child)"
              class="text-body-medium"
            />
          </v-list>
        </v-menu>

        <v-tab
          v-else
          :value="index"
          :to="getMenuItemTo(sub)"
        >
          {{ t(sub.locale_name) }}
        </v-tab>
      </template>
    </v-tabs>
  </div>
</template>
